import { Avatar, Card, Colors, Container, Heading, Icon, Pills, Text } from '@amelio/ui-library';

import type { User } from '@/types/directory/User';

import {
    ForArrowFrIllustration,
    ForArrowIllustration,
} from '@/components/Illustrations/Illustration';
import { UserCard } from '@/components/UserCard/UserCard';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import i18n from '@/plugins/i18n';
import { showErrorToast } from '@/plugins/Toast';
import { getFullName, getProfilePictureUrl } from '@/utils/user';

interface AssignReviewerHeaderProps {
    reviewees: User[];
    onRemoveReviewee?: (revieweeId: string) => void;
    isEvaluationLaunched?: boolean;
}

export const AssignReviewerHeader = ({
    reviewees,
    onRemoveReviewee,
    isEvaluationLaunched,
}: AssignReviewerHeaderProps) => {
    const { t } = useAmelioTranslations();
    const { getJobTitle } = useCompanyInformation();
    const isFrench = i18n.language.startsWith('fr');

    const handleRemoveReviewee = (revieweeId: string) => {
        if (isEvaluationLaunched) {
            showErrorToast({
                message: t('evaluation360AssignReviewers.header.cannotRemoveReviewee'),
            });
            return;
        }

        if (reviewees.length <= 1) {
            showErrorToast({
                message: t('evaluation360AssignReviewers.header.cannotRemoveLastReviewee'),
            });
            return;
        }

        onRemoveReviewee?.(revieweeId);
    };

    return (
        <Container className={'sm:flex'}>
            <Card
                className={'max-w-[600px] self-start px-8 py-8'}
                borderRadius={'2xl'}
            >
                <div className={'flex items-center gap-6'}>
                    <div
                        className={
                            'bg-primary-50 flex aspect-square h-16 w-16 shrink-0 items-center justify-center rounded-full'
                        }
                    >
                        <Icon
                            name={'arrow-repeat'}
                            size={32}
                            color={Colors.PRIMARY}
                        />
                    </div>

                    <div>
                        <Heading
                            level={3}
                            weight={'medium'}
                        >
                            {t('evaluation360AssignReviewers.header.feedback360')}
                        </Heading>

                        <Text
                            size={'small'}
                            className={'mt-1'}
                            color={Colors.BASE_600}
                        >
                            {t('evaluation360AssignReviewers.header.captureInsights')}
                        </Text>
                    </div>
                </div>
            </Card>

            <div className={'relative sm:ml-10 xl:ml-30'}>
                {isFrench ? (
                    <ForArrowFrIllustration
                        className={
                            'pointer-events-none absolute z-10 hidden w-100 rotate-12 sm:top-30 sm:-left-[300px] sm:!block xl:top-25 xl:-left-[325px]'
                        }
                    />
                ) : (
                    <ForArrowIllustration
                        className={
                            'pointer-events-none absolute z-10 hidden w-100 rotate-12 sm:top-30 sm:-left-[300px] sm:!block lg:top-25 lg:-left-[325px]'
                        }
                    />
                )}

                <Container
                    mt={'section'}
                    className={'flex-col justify-end'}
                >
                    <Card
                        className={'relative border-2'}
                        borderWidth={'thick'}
                        borderColor={Colors.SECONDARY}
                        borderRadius={'2xl'}
                    >
                        <Pills
                            className={'absolute -top-3.5 -right-3 text-center sm:-right-6'}
                            variant={'primary'}
                            size={'sm'}
                        >
                            {t('common.reviewees')}
                        </Pills>

                        <Container
                            direction={'col'}
                            gap={'sm'}
                            px={'sm'}
                            pb={'sm'}
                            mt={'xs'}
                            className={'scrollbar-glass max-h-[400px] overflow-y-auto'}
                        >
                            {reviewees.map((reviewee) => {
                                const fullName = getFullName(reviewee);

                                return (
                                    <Card
                                        className={
                                            'flex w-full flex-row items-center justify-between gap-2 bg-white py-2 pr-6 sm:px-2'
                                        }
                                        key={reviewee.id}
                                        padding={'none'}
                                        borderRadius={'2xl'}
                                    >
                                        <UserCard
                                            alt={fullName}
                                            avatar={
                                                <Avatar
                                                    alt={fullName}
                                                    src={getProfilePictureUrl(
                                                        reviewee?.profilePictureUrl || '',
                                                    )}
                                                    size={'xs'}
                                                />
                                            }
                                            description={
                                                <Text
                                                    size={'small'}
                                                    className={'mt-1 min-w-16 text-sm'}
                                                    color={Colors.BASE_500}
                                                >
                                                    {getJobTitle(reviewee.jobTitleId || '')}
                                                </Text>
                                            }
                                            name={fullName}
                                        />

                                        <div className={'flex shrink-0 items-center gap-2'}>
                                            <div
                                                className={
                                                    'bg-base-100 flex !h-10 !w-10 items-center justify-center rounded-2xl !p-1 sm:!h-14 sm:!w-14 sm:!p-4'
                                                }
                                            >
                                                <Icon
                                                    name={'design-layer'} // TODO: Update the icon according to business requirements
                                                    color={Colors.SECONDARY}
                                                    size={'lg'}
                                                />
                                            </div>
                                            {onRemoveReviewee && (
                                                <button
                                                    type={'button'}
                                                    aria-label={t(
                                                        'evaluation360AssignReviewers.header.removeReviewee',
                                                    )}
                                                    onClick={() =>
                                                        handleRemoveReviewee(reviewee.id)
                                                    }
                                                    className={
                                                        'flex cursor-pointer items-center justify-center rounded-2xl !pl-1'
                                                    }
                                                >
                                                    <Icon
                                                        name={'essential-close-circle'}
                                                        color={Colors.ERROR}
                                                        size={'lg'}
                                                    />
                                                </button>
                                            )}
                                        </div>
                                    </Card>
                                );
                            })}
                        </Container>
                    </Card>
                </Container>
            </div>
        </Container>
    );
};
